import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';
import Grid from '@mui/material/Grid';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  LinearProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Radio,
  Stack,
  Typography
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import TextInput from 'src/components/UI/input/TextInput';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext, ThemeContext } from 'src/store/ContextStore';
import ReportAlreadyHeldAlert, {
  reportAlreadyHeldFrom
} from 'src/components/common/ReportAlreadyHeldAlert';

const ButtonContainer = styled(Grid)({
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
  marginTop: 5
});

const OptionCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'accent'
})(({ theme, accent }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  cursor: 'pointer',
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(
    ['border-color', 'box-shadow', 'transform', 'background-color'],
    { duration: 160 }
  ),
  '&:hover, &:focus-visible': {
    borderColor: accent,
    backgroundColor: alpha(accent, 0.04),
    boxShadow: `0 4px 18px ${alpha(accent, 0.18)}`,
    transform: 'translateY(-2px)',
    outline: 'none'
  },
  '&:hover .option-chevron, &:focus-visible .option-chevron': {
    transform: 'translateX(3px)',
    opacity: 1
  }
}));

const VIEW = {
  choose: 'choose',
  newProband: 'new-proband',
  existingSearch: 'existing-search',
  done: 'done'
};

const fullName = (m) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ').trim();

const memberUpnLabel = (m) => {
  if (m.upn != null && m.upn !== '')
    return translate('UPN {{upn}}', { upn: m.upn });
  if (m.pid != null && m.pid !== '')
    return translate('UPN {{pid}}', { pid: m.pid });
  return m.familyMemberId || m.proBandId || '';
};

const memberLabel = (m) => {
  const name = fullName(m);
  const upn = memberUpnLabel(m);
  return name ? `${upn} — ${name}` : `${upn}`;
};

// Deterministic initials + a stable accent tint per member so the roster reads
// as people rather than rows.
const memberInitials = (m) => {
  const name = fullName(m);
  if (name) {
    const parts = name.split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
  }
  if (m.upn) return String(m.upn).slice(0, 2).toUpperCase();
  if (m.pid != null) return String(m.pid).slice(0, 2).toUpperCase();
  return '#';
};

const AVATAR_TINTS = ['#2ba9bf', '#7b68ee', '#e8893b', '#53cd53', '#d8527c'];
const tintFor = (m) => {
  const seed = String(m.pid ?? m.upn ?? m.familyMemberId ?? '0');
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1)
    hash = (hash + seed.charCodeAt(i)) | 0;
  return AVATAR_TINTS[Math.abs(hash) % AVATAR_TINTS.length];
};

const MigrateModal = ({
  open,
  handleClose,
  members,
  sourceUserId,
  sourceFamilyId,
  onSuccess
}) => {
  const t = useAutoTranslation();

  const { setSnack } = useContext(SnackContext);
  const { theme: appTheme } = useContext(ThemeContext);
  const accent = appTheme?.accent || '#2ba9bf';
  const [view, setView] = useState(VIEW.choose);
  const [loading, setLoading] = useState(false);
  const [probandPid, setProbandPid] = useState(null);
  const [familyIds, setFamilyIds] = useState([]);
  const [destFamily, setDestFamily] = useState(null);
  const [resultFamilyId, setResultFamilyId] = useState(null);
  // The server's 409 REPORT_ALREADY_HELD: the chosen family already holds one of
  // the moving people's test reports, so the move cannot go ahead. Shown in the
  // dialog (see ReportAlreadyHeldAlert) until another family is chosen.
  const [refusal, setRefusal] = useState(null);

  const rows = useMemo(
    () => (members || []).map((m) => ({ ...m, label: memberLabel(m) })),
    [members]
  );
  const selectedPids = useMemo(() => rows.map((m) => m.pid), [rows]);
  // Send the live member objects (relationship + person data) so the backend
  // validates and migrates against exactly what the user sees on the canvas —
  // never a possibly-stale stored snapshot. Strip layout/runtime-only fields.
  const payloadMembers = useMemo(
    () =>
      (members || []).map((m) => {
        const rest = { ...m };
        // Layout/runtime-only or identity fields the backend re-derives.
        [
          'x',
          'y',
          'nodeRef',
          'kind',
          'label',
          '_id',
          'member_id',
          '__safeContent__',
          'isDynamicallyAdded'
        ].forEach((k) => delete rest[k]);
        return rest;
      }),
    [members]
  );

  useEffect(() => {
    if (!open) return;
    setView(VIEW.choose);
    setLoading(false);
    setProbandPid(rows.length === 1 ? rows[0].pid : null);
    setDestFamily(null);
    setResultFamilyId(null);
    setRefusal(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFamilyIds = async () => {
    try {
      const resp = await axios.get(APICONSTANTS.getProbandUsersFamilyIds);
      const list = resp?.data?.data || [];
      setFamilyIds(list.filter((f) => String(f._id) !== String(sourceUserId)));
    } catch (err) {
      console.error('Failed to load families', err);
      setSnack({
        status: true,
        msg: t('Failed to load families'),
        severity: 'error'
      });
    }
  };

  const destFamilyOptions = useMemo(
    () => familyIds.map((f) => ({ label: f.familyId, value: f._id })),
    [familyIds]
  );

  const onChooseExisting = () => {
    setView(VIEW.existingSearch);
    loadFamilyIds();
  };

  const migrateToNew = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(APICONSTANTS.migrateToNewFamily, {
        sourceUserId,
        sourceFamilyId,
        selectedPids,
        newProbandPid: probandPid,
        members: payloadMembers
      });
      setResultFamilyId(resp?.data?.newFamilyId);
      setView(VIEW.done);
      setSnack({
        status: true,
        msg: t('Members migrated to a new pedigree'),
        severity: 'success'
      });
    } catch (err) {
      console.error('migrateToNew error', err);
      setSnack({
        status: true,
        msg: err?.response?.data?.message || t('Migration failed'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const migrateToExisting = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(APICONSTANTS.migrateToExistingFamily, {
        sourceUserId,
        sourceFamilyId,
        selectedPids,
        destUserId: destFamily.value,
        destFamilyId: destFamily.label,
        members: payloadMembers
      });
      const destFamilyId = resp?.data?.destFamilyId || destFamily.label;
      setResultFamilyId(destFamilyId);
      setView(VIEW.done);
      setSnack({
        status: true,
        msg: t('Members migrated to {{destFamilyId}}', { destFamilyId }),
        severity: 'success'
      });
    } catch (err) {
      const held = reportAlreadyHeldFrom(err);
      if (held) {
        setRefusal(held);
        return;
      }
      console.error('migrateToExisting error', err);
      setSnack({
        status: true,
        msg: err?.response?.data?.message || t('Migration failed'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const finishAndClose = () => {
    onSuccess?.(resultFamilyId);
    handleClose();
  };

  const MemberSummary = () => {
    const shown = rows.slice(0, 5);
    const extra = rows.length - shown.length;
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          borderColor: alpha(accent, 0.3),
          backgroundColor: alpha(accent, 0.05)
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: 'center'
          }}
        >
          <Avatar sx={{ bgcolor: accent, width: 38, height: 38 }}>
            <GroupsOutlinedIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {`${rows.length} ${
                rows.length === 1 ? t('member') : t('members')
              } ${t('selected')}`}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary'
              }}
            >
              {t('Migrating out of family')}{' '}
              <Box
                component="span"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {sourceFamilyId}
              </Box>
            </Typography>
          </Box>
        </Stack>
        {shown.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}
          >
            {shown.map((m) => (
              <Chip
                key={String(m.pid)}
                size="small"
                avatar={
                  <Avatar
                    sx={{
                      bgcolor: `${tintFor(m)} !important`,
                      color: '#fff !important'
                    }}
                  >
                    {memberInitials(m)}
                  </Avatar>
                }
                label={fullName(m) || m.label}
                variant="outlined"
                sx={{ maxWidth: 200 }}
              />
            ))}
            {extra > 0 && (
              <Chip size="small" label={`+${extra}`} sx={{ fontWeight: 600 }} />
            )}
          </Stack>
        )}
      </Paper>
    );
  };

  const renderBody = () => {
    if (view === VIEW.choose) {
      return (
        <Fade in timeout={250}>
          <Stack spacing={2.5}>
            <MemberSummary />
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary'
              }}
            >
              {t('Where should these members go?')}
            </Typography>
            <OptionCard
              accent={accent}
              elevation={0}
              tabIndex={0}
              role="button"
              onClick={() => setView(VIEW.newProband)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && setView(VIEW.newProband)
              }
            >
              <Avatar
                variant="rounded"
                sx={{
                  bgcolor: alpha(accent, 0.12),
                  color: accent,
                  width: 46,
                  height: 46
                }}
              >
                <AccountTreeOutlinedIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t('Migrate as new pedigree')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary'
                  }}
                >
                  {t('Create a brand-new family with these members')}
                </Typography>
              </Box>
              <ChevronRightIcon
                className="option-chevron"
                sx={{ color: accent, opacity: 0.5, transition: 'all .16s' }}
              />
            </OptionCard>
            <OptionCard
              accent={accent}
              elevation={0}
              tabIndex={0}
              role="button"
              onClick={onChooseExisting}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && onChooseExisting()
              }
            >
              <Avatar
                variant="rounded"
                sx={{
                  bgcolor: alpha(accent, 0.12),
                  color: accent,
                  width: 46,
                  height: 46
                }}
              >
                <FolderSharedOutlinedIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t('Move into an existing family')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary'
                  }}
                >
                  {t('Search and merge them into another pedigree')}
                </Typography>
              </Box>
              <ChevronRightIcon
                className="option-chevron"
                sx={{ color: accent, opacity: 0.5, transition: 'all .16s' }}
              />
            </OptionCard>
          </Stack>
        </Fade>
      );
    }

    if (view === VIEW.newProband) {
      return (
        <Fade in timeout={250}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('Select the proband for the new family')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {t(
                  'This member becomes the starting point of the new pedigree'
                )}
              </Typography>
            </Box>
            <List
              dense
              sx={{
                maxHeight: 260,
                overflowY: 'auto',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 0.5
              }}
            >
              {rows.map((m) => {
                const checked = String(probandPid) === String(m.pid);
                return (
                  <ListItemButton
                    key={String(m.pid)}
                    selected={checked}
                    onClick={() => setProbandPid(m.pid)}
                    sx={{
                      borderRadius: 1.5,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: alpha(accent, 0.12),
                        '&:hover': { backgroundColor: alpha(accent, 0.18) }
                      }
                    }}
                  >
                    <Radio
                      edge="start"
                      checked={checked}
                      tabIndex={-1}
                      sx={{ color: accent, '&.Mui-checked': { color: accent } }}
                    />
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        sx={{
                          bgcolor: tintFor(m),
                          width: 34,
                          height: 34,
                          fontSize: 14
                        }}
                      >
                        {memberInitials(m)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={fullName(m) || m.label}
                      secondary={memberUpnLabel(m)}
                      slotProps={{
                        primary: {
                          fontWeight: checked ? 600 : 400
                        }
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
            <ButtonContainer size={12}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => setView(VIEW.choose)}
                disabled={loading}
              >
                {t('Back')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={migrateToNew}
                disabled={loading || probandPid === null}
                endIcon={loading ? <CircularProgress size={15} /> : undefined}
              >
                {t('Migrate')}
              </Button>
            </ButtonContainer>
          </Stack>
        </Fade>
      );
    }

    if (view === VIEW.existingSearch) {
      return (
        <Fade in timeout={250}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('Select the family to migrate the members into')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {t('The members will be added to the chosen pedigree')}
              </Typography>
            </Box>
            <TextInput
              label="Destination family"
              type="simple-autocomplete"
              name="destFamily"
              value={destFamily || null}
              onChange={(e, newValue) => {
                setDestFamily(newValue);
                setRefusal(null);
              }}
              options={destFamilyOptions}
              id="migrate-dest-family"
            />
            {refusal && <ReportAlreadyHeldAlert refusal={refusal} />}
            <ButtonContainer size={12}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => setView(VIEW.choose)}
                disabled={loading}
              >
                {t('Back')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={migrateToExisting}
                disabled={loading || !destFamily || !!refusal}
                endIcon={loading ? <CircularProgress size={15} /> : undefined}
              >
                {t('Migrate')}
              </Button>
            </ButtonContainer>
          </Stack>
        </Fade>
      );
    }

    // VIEW.done
    return (
      <Fade in timeout={300}>
        <Stack
          spacing={2.5}
          sx={{
            alignItems: 'center',
            py: 2
          }}
        >
          <Avatar
            sx={{
              bgcolor: alpha('#53cd53', 0.15),
              color: '#3ba33b',
              width: 64,
              height: 64
            }}
          >
            <CheckCircleRoundedIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('Migration complete')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.5
              }}
            >
              {`${rows.length} ${
                rows.length === 1 ? t('member') : t('members')
              } ${t('moved to')} `}
              <Box
                component="span"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {resultFamilyId}
              </Box>
            </Typography>
          </Box>
          <ButtonContainer
            item
            xs={12}
            sx={{ justifyContent: 'center', width: '100%' }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={finishAndClose}
            >
              {t('Close')}
            </Button>
            {resultFamilyId && (
              <Button
                variant="contained"
                color="secondary"
                endIcon={<OpenInNewRoundedIcon />}
                component={Link}
                to={CONSTANTS.pedigreeChartPath.replace(':_id', resultFamilyId)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onSuccess?.(resultFamilyId)}
              >
                {t('Open pedigree')}
              </Button>
            )}
          </ButtonContainer>
        </Stack>
      </Fade>
    );
  };

  return (
    <CustomDialog
      open={open}
      title={'Migrate Members'}
      handleClose={handleClose}
      minHeight={200}
      distanceTop={120}
      allowBackgroundClose={!loading}
      showCancelBtn={false}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {renderBody()}
    </CustomDialog>
  );
};

export default MigrateModal;
